import React, { useRef, useState, useEffect } from 'react';
import api from '../services/api';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import PageHero from '../components/layout/PageHero';
import ScrollReveal from '../components/ui/ScrollReveal';
import { companyInfo, leasingLoansProducts } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, Mail, Download, ArrowRight, ChevronDown, Car, Wallet, Building2, Home, GraduationCap, FileText, Coins, Percent, Loader2 } from 'lucide-react';
const ProductsPage = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const sectionRefs = useRef({});

  useEffect(() => {
    const unsubscribe = api.products.subscribeToProducts((productsData) => {
      setProducts(productsData || []);
      setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const categories = [
    { name: 'All Products', slug: 'all', icon: null },
    { name: 'Leasing & Loans', slug: 'leasing-loans', icon: 'Car' },
    { name: 'Gold Loan', slug: 'gold-loan', icon: 'Coins' }
  ];

  const iconMap = {
    Car, Wallet, Building2, Home, GraduationCap, FileText, Coins, Percent
  };

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => {
      if (activeCategory === 'leasing-loans') {
        return p.category === 'Leasing & Loans';
      } else if (activeCategory === 'gold-loan') {
        return p.category === 'Gold Loan';
      }
      return true;
    });

  // Hover to scroll functionality
  const handleProductHover = (productSlug) => {
    setHoveredProduct(productSlug);
    if (sectionRefs.current[productSlug]) {
      sectionRefs.current[productSlug].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Our Products - {companyInfo.name}</title>
        <meta name="description" content="Explore our comprehensive range of financial products including leasing, loans, and gold loans." />
      </Helmet>

      <Header />
      <SocialSidebar />
      <main className="pb-16">
        {/* Hero with background image */}
        <PageHero
          title={t('pages.productsTitle')}
          subtitle={t('pages.productsSubtitle')}
          breadcrumbs={[
            { label: t('nav.home'), link: '/' },
            { label: t('nav.products') }
          ]}
          backgroundImage="/products-bg.jpg"
        />

        <div className="max-w-7xl mx-auto px-4 py-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#2A9D8F] animate-spin" />
            </div>
          ) : (
            <>
              {/* Category Filter */}
              <div className="flex flex-wrap gap-3 mb-10">
                {categories.map((cat) => {
                  const Icon = cat.icon ? iconMap[cat.icon] : null;
                  return (
                    <button
                      key={cat.slug}
                      onClick={() => setActiveCategory(cat.slug)}
                      className={`px-6 py-2 rounded-full font-medium transition-all flex items-center gap-2 ${activeCategory === cat.slug
                        ? 'bg-[#2A9D8F] text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 hover:border-[#2A9D8F]'
                        }`}
                    >
                      {Icon && <Icon size={18} />}
                      {cat.name}
                    </button>
                  );
                })}
              </div>

          {/* Leasing & Loans Quick Access */}
          {(activeCategory === 'all' || activeCategory === 'leasing-loans') && (
            <ScrollReveal animation="fade-left" delay={100}>
              <div className="mb-12 bg-gradient-to-r from-[#2A9D8F]/5 to-[#004488]/5 rounded-xl p-6 border border-[#2A9D8F]/20">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Car size={24} className="text-[#2A9D8F]" />
                    Leasing & Loans
                  </h2>
                  <Link
                    to="/products/leasing-loans"
                    className="text-[#2A9D8F] font-medium flex items-center hover:underline"
                  >
                    View All <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {leasingLoansProducts.map((product) => {
                    const Icon = iconMap[product.icon] || Car;
                    return (
                      <Link
                        key={product.slug}
                        to={`/products/leasing-loans/${product.slug}`}
                        className="bg-white rounded-lg p-4 text-center hover:shadow-lg transition-all hover:scale-105 border border-gray-100 group"
                      >
                        <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-[#2A9D8F] transition-colors">
                          <Icon size={24} className="text-[#2A9D8F] group-hover:text-white transition-colors" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 group-hover:text-[#2A9D8F]">
                          {product.title}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </ScrollReveal>
          )}

          {/* Products Grid with Scroll Reveal */}
          <ScrollReveal animation="fade-up" delay={150}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  ref={(el) => (sectionRefs.current[product.slug] = el)}
                  onMouseEnter={() => handleProductHover(product.slug)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  className={`bg-white rounded-lg overflow-hidden shadow-md transition-all duration-300 group ${hoveredProduct === product.slug
                    ? 'shadow-2xl scale-[1.02] ring-2 ring-[#2A9D8F]'
                    : 'hover:shadow-xl'
                    }`}
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title}
                      className={`w-full h-full object-cover transition-transform duration-500 ${hoveredProduct === product.slug ? 'scale-110' : 'group-hover:scale-105'
                        }`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity ${hoveredProduct === product.slug ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`} />
                    {product.category && (
                      <span className="absolute top-3 left-3 bg-white/90 text-xs px-3 py-1 rounded-full text-gray-700 font-medium">
                        {product.category}
                      </span>
                    )}
                    {product.hasCalculator && (
                      <span className="absolute top-3 right-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                        Calculator Available
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`font-semibold text-xl mb-2 transition-colors ${hoveredProduct === product.slug ? 'text-[#2A9D8F]' : 'text-gray-900 group-hover:text-[#2A9D8F]'
                      }`}>
                      {product.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2">
                      {product.shortDescription}
                    </p>

                    {/* Expanded details on hover */}
                    <div className={`overflow-hidden transition-all duration-300 ${hoveredProduct === product.slug ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                      <div className="border-t border-gray-100 pt-4 mb-4">
                        <p className="text-sm text-gray-600">
                          <strong>Features:</strong> {product.features?.slice(0, 2).join(', ')}...
                        </p>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-3">
                        <a
                          href={`tel:${companyInfo.phone}`}
                          className="p-2 text-gray-400 hover:text-[#2A9D8F] transition-colors bg-gray-50 rounded-full"
                          title="Call us"
                        >
                          <Phone size={18} />
                        </a>
                        <a
                          href={`mailto:${companyInfo.email}`}
                          className="p-2 text-gray-400 hover:text-[#2A9D8F] transition-colors bg-gray-50 rounded-full"
                          title="Email us"
                        >
                          <Mail size={18} />
                        </a>
                        {product.hasApplication && (
                          <button
                            className="p-2 text-gray-400 hover:text-[#2A9D8F] transition-colors bg-gray-50 rounded-full"
                            title="Download application"
                          >
                            <Download size={18} />
                          </button>
                        )}
                      </div>
                      <Link
                        to={product.category === 'Leasing & Loans'
                          ? `/products/leasing-loans/${product.slug}`
                          : `/products/${product.slug}`}
                        className="text-[#2A9D8F] font-medium flex items-center text-sm hover:underline"
                      >
                        Learn More
                        <ArrowRight size={14} className="ml-1" />
                      </Link>
                    </div>
                  </div>

                  {/* Color bar */}
                  <div
                    className={`h-1 transition-all duration-300 ${hoveredProduct === product.slug ? 'h-2' : ''
                      }`}
                    style={{ backgroundColor: product.color }}
                  />
                </div>
              ))}
            </div>
          </ScrollReveal>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductsPage;
